import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "mb-3 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700" }
const _hoisted_2 = {
  key: 0,
  class: "grid grid-cols-2 gap-3 sm:grid-cols-3"
}
const _hoisted_3 = {
  key: 1,
  class: "rounded-2xl border border-dashed border-red-200 bg-red-50 px-6 py-8 text-center"
}
const _hoisted_4 = { class: "text-sm font-semibold text-red-800" }
const _hoisted_5 = { class: "mt-1 text-xs text-red-700/80" }
const _hoisted_6 = {
  key: 2,
  class: "rounded-2xl border border-dashed border-gray-300 bg-white/40 px-6 py-8 text-center"
}
const _hoisted_7 = { class: "text-sm font-semibold text-gray-600" }
const _hoisted_8 = { class: "mt-1 text-xs text-gray-500" }
const _hoisted_9 = {
  key: 3,
  class: "grid grid-cols-2 gap-3 sm:grid-cols-3"
}
const _hoisted_10 = ["onClick"]
const _hoisted_11 = {
  key: 0,
  class: "absolute -top-2 left-2 z-10 rounded-full bg-teal-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-md"
}
const _hoisted_12 = ["src", "alt"]
const _hoisted_13 = {
  key: 1,
  class: "absolute left-2 top-2 z-10 inline-flex items-center gap-1 rounded-md bg-red-600/95 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-white"
}
const _hoisted_14 = { class: "absolute left-1/2 top-1/2 z-10 flex size-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90" }
const _hoisted_15 = { class: "absolute left-2 bottom-1.5 z-10 truncate text-[11px] font-bold text-white drop-shadow" }
const _hoisted_16 = {
  key: 2,
  class: "absolute right-1.5 top-1.5 z-20 flex size-5 items-center justify-center rounded-full bg-teal-600 text-white shadow-md"
}
const _hoisted_17 = { class: "mt-4 flex flex-wrap items-center gap-4" }
const _hoisted_18 = ["disabled"]

import {
  computed, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  PlayIcon, SparklesIcon, CheckIcon,
} from '@heroicons/vue/24/solid';
import { ExclamationTriangleIcon, VideoCameraSlashIcon } from '@heroicons/vue/24/outline';
import useAuthStore from '@/store/auth';
import useLivviaStore from '@/store/livvia';
import usePluginStore from '@/store/plugin/plugin';
import { InstagramMedia, isVideoMedia } from '@/models/plugin/transcription';


export default /*@__PURE__*/_defineComponent({
  __name: 'VideoPicker',
  setup(__props) {

const { t } = useI18n();
const { session } = useAuthStore();
const livvia = useLivviaStore();
const { pluginGetInstagramMedias } = usePluginStore();

const accountHandle = computed(() => {
  const name = session?.name?.split(' ')[0] || 'voce';
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
});

const videos = ref<InstagramMedia[]>([]);
const selectedId = ref<string>('');
const loading = ref(true);
const error = ref('');

const fallbackGradient = (i: number): string => {
  const palettes = [
    'bg-gradient-to-br from-orange-300 via-pink-500 to-purple-600',
    'bg-gradient-to-br from-sky-400 via-indigo-500 to-purple-600',
    'bg-gradient-to-br from-emerald-300 via-sky-400 to-indigo-500',
    'bg-gradient-to-br from-teal-400 via-sky-500 to-indigo-500',
    'bg-gradient-to-br from-amber-300 via-pink-400 to-purple-500',
    'bg-gradient-to-br from-emerald-200 via-sky-300 to-indigo-400',
  ];
  return palettes[i % palettes.length];
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const loadMedias = async () => {
  const pluginId = livvia.lastLive?.pluginId;
  if (!pluginId) {
    error.value = t('livvia.videoPicker.noPluginError');
    loading.value = false;
    return;
  }
  loading.value = true;
  error.value = '';
  try {
    const resp = await pluginGetInstagramMedias({ pluginId, page: 1, limit: 12 });
    videos.value = resp.data || [];
    // Pre-select the "pending" media if it's in the list, else the first video.
    const preferredId = livvia.lastLive?.mediaId;
    const preferred = preferredId ? videos.value.find((v) => v.id === preferredId) : null;
    selectedId.value = preferred?.id || videos.value[0]?.id || '';
  } catch (e: any) {
    error.value = e?.message || t('livvia.videoPicker.errorSubtitle');
  } finally {
    loading.value = false;
  }
};

const onGenerate = () => {
  const mediaId = selectedId.value;
  const pluginId = livvia.lastLive?.pluginId;
  if (!mediaId || !pluginId) return;
  livvia.startProcessing({ pluginId, mediaId });
};

const onBack = () => {
  if (livvia.lastLive) {
    livvia.enterPostLive(livvia.lastLive);
  } else {
    livvia.reset();
  }
};

onMounted(loadMedias);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _cache[0] || (_cache[0] = _createElementVNode("svg", {
        viewBox: "0 0 24 24",
        class: "size-3.5",
        fill: "none",
        stroke: "currentColor",
        "stroke-width": "2",
        "stroke-linecap": "round",
        "stroke-linejoin": "round"
      }, [
        _createElementVNode("rect", {
          x: "2",
          y: "2",
          width: "20",
          height: "20",
          rx: "6"
        }),
        _createElementVNode("circle", {
          cx: "12",
          cy: "12",
          r: "4"
        }),
        _createElementVNode("circle", {
          cx: "17.5",
          cy: "6.5",
          r: "1.3",
          fill: "currentColor",
          stroke: "none"
        })
      ], -1)),
      _createTextVNode(" " + _toDisplayString(_unref(t)('livvia.videoPicker.accountChip', { handle: accountHandle.value })), 1)
    ]),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(6, (i) => {
            return _createElementVNode("div", {
              key: i,
              class: "aspect-square animate-pulse rounded-2xl bg-gray-200"
            })
          }), 64))
        ]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_unref(ExclamationTriangleIcon), { class: "mx-auto mb-2 size-6 text-red-500" }),
            _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(t)('livvia.videoPicker.errorTitle')), 1),
            _createElementVNode("p", _hoisted_5, _toDisplayString(error.value), 1),
            _createElementVNode("button", {
              type: "button",
              class: "mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100",
              onClick: loadMedias
            }, _toDisplayString(_unref(t)('livvia.videoPicker.retry')), 1)
          ]))
        : (!videos.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(_unref(VideoCameraSlashIcon), { class: "mx-auto mb-2 size-6 text-gray-400" }),
              _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(t)('livvia.videoPicker.emptyTitle')), 1),
              _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(t)('livvia.videoPicker.emptySubtitle')), 1)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_9, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(videos.value, (v, i) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: v.id,
                  type: "button",
                  class: "group relative text-left",
                  onClick: ($event: any) => (selectedId.value = v.id)
                }, [
                  (v.id === _unref(livvia).lastLive?.mediaId)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(_unref(t)('livvia.videoPicker.flagLatest')), 1))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", {
                    class: _normalizeClass(["relative aspect-square overflow-hidden rounded-2xl border-2 shadow-sm transition", [
            selectedId.value === v.id
              ? 'border-teal-500 ring-2 ring-teal-200'
              : 'border-transparent',
            v.thumbnail_url ? '' : fallbackGradient(i),
          ]])
                  }, [
                    (v.thumbnail_url)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: v.thumbnail_url,
                          alt: v.caption || v.id,
                          class: "absolute inset-0 h-full w-full object-cover",
                          loading: "lazy"
                        }, null, 8, _hoisted_12))
                      : _createCommentVNode("", true),
                    _cache[2] || (_cache[2] = _createElementVNode("span", { class: "absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" }, null, -1)),
                    (_unref(isVideoMedia)(v))
                      ? (_openBlock(), _createElementBlock("span", _hoisted_13, [
                          _cache[1] || (_cache[1] = _createElementVNode("span", { class: "size-1 rounded-full bg-white" }, null, -1)),
                          _createTextVNode(" " + _toDisplayString(_unref(t)('livvia.videoPicker.liveTag')), 1)
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("span", _hoisted_14, [
                      _createVNode(_unref(PlayIcon), { class: "ml-0.5 size-3.5 text-gray-900" })
                    ]),
                    _createElementVNode("span", _hoisted_15, _toDisplayString(formatDate(v.timestamp)), 1),
                    (selectedId.value === v.id)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_16, [
                          _createVNode(_unref(CheckIcon), { class: "size-3" })
                        ]))
                      : _createCommentVNode("", true)
                  ], 2)
                ], 8, _hoisted_10))
              }), 128))
            ])),
    _createElementVNode("div", _hoisted_17, [
      _createElementVNode("button", {
        type: "button",
        class: "inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:cursor-not-allowed disabled:opacity-60",
        disabled: !selectedId.value || loading.value,
        onClick: onGenerate
      }, [
        _createVNode(_unref(SparklesIcon), { class: "size-4" }),
        _createTextVNode(" " + _toDisplayString(_unref(t)('livvia.videoPicker.actionGenerate')), 1)
      ], 8, _hoisted_18),
      _createElementVNode("button", {
        type: "button",
        class: "text-sm font-semibold text-gray-500 underline underline-offset-2 hover:text-gray-700",
        onClick: onBack
      }, _toDisplayString(_unref(t)('livvia.videoPicker.actionBack')), 1)
    ])
  ]))
}
}

})